import {ChangeDetectorRef, Component, inject, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {LetDirective} from '@ngrx/component';
import {Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeChipModule} from 'se-fe-chip';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFeModalModule} from 'se-fe-modal';
import {SeFeOverlayModule} from 'se-fe-overlay';
import {SeFePageModule} from 'se-fe-page';
import {SeFePositionModule, SeFePositionService} from 'se-fe-position';
import {SeFeToastModule} from 'se-fe-toast';
import {ItemMediaCardComponent} from '../../../../common/components/item-media-card/item-media-card.component';
import {ItemOrgCardComponent} from '../../../../common/components/item-org-card/item-org-card.component';
import {FullPageSpinnerComponent} from '../../../../common/components/full-page-spinner/full-page-spinner.component';
import {Bundle} from '@sportngin/se-commerce-sdk/models/clare';
import {BundleProductsUpdateProduct} from '../../../../models/clare/bundle';
import {selectProducts} from '../../../product-list/store/product-list.selectors';
import {
  ProductOrganizationUpdate,
  ProductImagesUpdate,
  UpdateProductDetail,
} from '../../../product/components/product-detail/product-detail.store';
import {IncludedProductsCardComponent} from '../included-products-card/included-products-card.component';
import {BundleDetailStore, BundleDetailVm} from './bundle-detail.store';
import {BundleDetailsCardComponent} from './components/bundle-details-card/bundle-details-card.component';
import {ProductsSelectedEvent} from '../included-products-card/choose-bundle-products-modal/choose-bundle-products-modal.component';
import {getProductId, Product} from '../../../../models/clare';

@Component({
  selector: 'bundle-detail',
  imports: [
    BundleDetailsCardComponent,
    FullPageSpinnerComponent,
    IncludedProductsCardComponent,
    ItemMediaCardComponent,
    ItemOrgCardComponent,
    LetDirective,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeEmptyStateModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeMenuModule,
    SeFeModalModule,
    SeFeOverlayModule,
    SeFePageModule,
    SeFePositionModule,
    SeFeToastModule,
  ],
  providers: [SeFePositionService, BundleDetailStore],
  templateUrl: './bundle-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bundle-detail.component.scss',
})
export class BundleDetailComponent implements OnDestroy {
  products$: Observable<Product[] | undefined>;
  bundleProducts: Product[] = [];
  bundleDetailStore: BundleDetailStore;

  bundleStatusMenuOptions: SeFeMenuOptions = {
    name: 'Product bundle status',
    sections: [
      {
        menuItems: [
          {
            text: 'Active',
            action: () => this.setBundleStatus(true),
          },
          {
            text: 'Inactive',
            action: () => this.setBundleStatus(false),
          },
        ],
      },
    ],
  };
  vm$: Observable<BundleDetailVm>;
  error$: Observable<string | null>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private store: Store
  ) {
    this.bundleDetailStore = inject(BundleDetailStore);

    this.vm$ = this.bundleDetailStore.vm$;
    this.error$ = this.bundleDetailStore.error$;
    this.bundleDetailStore.loadBundle();

    this.products$ = this.store.select(selectProducts);

    this.bundleDetailStore.bundleProducts$.pipe(takeUntil(this.destroy$)).subscribe(products => {
      this.bundleProducts = products ?? [];
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setBundleStatus(active: boolean): void {
    this.bundleDetailStore.updateProductStatus(active);
  }

  getStatusChipStyle(bundleStatus: boolean, synced: boolean): string {
    if (!synced) {
      return 'caution';
    }
    return bundleStatus ? 'done' : 'off';
  }

  getStatusChipText(bundleStatus: boolean): string {
    return bundleStatus ? 'Active' : 'Inactive';
  }

  /**
   * Updates the product and price break using the component store.
   * @param event - The update event containing the new product and price break.
   */
  updateProductAndPriceBreak(event: UpdateProductDetail<{bundle: Partial<Bundle>}>): void {
    this.bundleDetailStore.updateBundle(event);
  }

  /**
   * Updates the product images using the component store.
   * @param event - The update event containing the images array.
   */
  updateProductImageAndDescription(event: UpdateProductDetail<ProductImagesUpdate>): void {
    this.bundleDetailStore.updateProductImageAndDescription(event);
  }

  /**
   * Sets the error state in the component store.
   * @param event - An object containing the error message or null.
   */
  setError(event: {error: string | null}): void {
    this.bundleDetailStore.setError(event.error);
  }

  saveBundleCatalogs(event: UpdateProductDetail<ProductOrganizationUpdate>): void {
    this.bundleDetailStore.saveBundleCatalogs(event);
  }

  loadOrganizationCatalogs(): void {
    this.bundleDetailStore.loadOrganizationCatalogs();
  }

  /**
   * Handles products selected from the choose-bundle-products-modal.
   * Computes added and removed product IDs, then dispatches a single sequential
   * bundle update via the store (remove → add → price) to avoid race conditions
   * when both additions and removals are needed in the same edit session.
   *
   * @param event The products selected for the bundle.
   */
  handleProductsSelected(event: ProductsSelectedEvent): void {
    const {products, price} = event;
    const {productsToAdd, productsToRemove} = this.computeProductDiff(products);
    this.dispatchBundleProductChanges(productsToAdd, productsToRemove, price);
  }

  /**
   * Computes the diff between the current bundle products and the newly selected products.
   * Returns arrays of products to add and remove.
   */
  private computeProductDiff(selectedProducts: Product[]): {
    productsToAdd: BundleProductsUpdateProduct[];
    productsToRemove: BundleProductsUpdateProduct[];
  } {
    const safeProducts: Product[] = Array.isArray(selectedProducts) ? selectedProducts : [];
    const previousProducts: Product[] = Array.isArray(this.bundleProducts) ? this.bundleProducts : [];

    const previousIdSet = new Set(
      previousProducts.map(p => getProductId(p)).filter((id): id is string => id !== undefined && id !== null)
    );
    const nextIdSet = new Set(
      safeProducts.map(p => getProductId(p)).filter((id): id is string => id !== undefined && id !== null)
    );

    const productsToAdd = this.findNewProducts(nextIdSet, previousIdSet);
    const productsToRemove = this.findRemovedProducts(previousIdSet, nextIdSet);

    return {productsToAdd, productsToRemove};
  }

  private findNewProducts(nextIds: Set<string>, previousIds: Set<string>): BundleProductsUpdateProduct[] {
    const result: BundleProductsUpdateProduct[] = [];
    nextIds.forEach(id => {
      const parsed = Number(id);
      if (!previousIds.has(id) && !Number.isNaN(parsed)) {
        result.push({product_id: parsed});
      }
    });
    return result;
  }

  private findRemovedProducts(previousIds: Set<string>, nextIds: Set<string>): BundleProductsUpdateProduct[] {
    const result: BundleProductsUpdateProduct[] = [];
    previousIds.forEach(id => {
      const parsed = Number(id);
      if (!nextIds.has(id) && !Number.isNaN(parsed)) {
        result.push({product_id: parsed});
      }
    });
    return result;
  }

  /**
   * Dispatches the appropriate store effect(s) based on what changed.
   * Uses the combined effect when multiple operation types are needed to avoid race conditions.
   */
  private dispatchBundleProductChanges(
    productsToAdd: BundleProductsUpdateProduct[],
    productsToRemove: BundleProductsUpdateProduct[],
    price: number | undefined
  ): void {
    const hasAdds = productsToAdd.length > 0;
    const hasRemoves = productsToRemove.length > 0;
    const hasPrice = typeof price === 'number' && Number.isFinite(price);

    // When both adds and removes are needed (or price change is also involved),
    // use the combined effect to chain them sequentially and avoid race conditions.
    if ((hasAdds && hasRemoves) || ((hasAdds || hasRemoves) && hasPrice)) {
      this.bundleDetailStore.updateBundleProducts({
        productsToAdd,
        productsToRemove,
        price: hasPrice ? price : undefined,
      });
    } else {
      // Only one type of operation — safe to dispatch individually.
      if (hasPrice) {
        this.bundleDetailStore.updateBundle({data: {bundle: {price_cents: Math.round(price! * 100)}}});
      }
      if (hasAdds) {
        this.bundleDetailStore.addProductsToBundle(productsToAdd);
      }
      if (hasRemoves) {
        this.bundleDetailStore.removeProductsFromBundle(productsToRemove);
      }
    }
  }

  /**
   * Handles a single product being removed from the bundle table.
   * Dispatches a remove-products effect and waits for the store to publish the
   * confirmed bundle membership from the backend response.
   *
   * @param product The product removed from the bundle.
   */
  handleProductRemoved(product: Product): void {
    const productId = getProductId(product);
    const parsedId = productId !== undefined && productId !== null ? Number(productId) : NaN;
    const hasValidId = !Number.isNaN(parsedId);
    if (hasValidId) {
      const priceDollars = this.getBundleProductPrice(product);
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const price_cents = Math.round(priceDollars * 100);
      this.bundleDetailStore.removeProductsFromBundle([{product_id: parsedId, price_cents}]);
    }
  }

  setAllProductsToActive(): void {
    this.bundleDetailStore.setAllProductsToActive();
  }

  /**
   * Returns the bundle-specific price for a product in dollars.
   * Prefers `bundledPrice` when present, falling back to `price`, otherwise 0.
   */
  private getBundleProductPrice(product: Product | undefined): number {
    if (!product) {
      return 0;
    }
    const bundlePrice = typeof product.bundledPrice === 'number' ? product.bundledPrice : undefined;
    const defaultPrice = typeof product.price === 'number' ? product.price : undefined;
    if (bundlePrice !== undefined && !Number.isNaN(bundlePrice)) {
      return bundlePrice;
    }
    if (defaultPrice !== undefined && !Number.isNaN(defaultPrice)) {
      return defaultPrice;
    }
    return 0;
  }
}
